import React, { useState, useEffect, useCallback, FC } from 'react';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import { makeStyles } from '@material-ui/styles';
import SearchIcon from '@material-ui/icons/Search';
import PersonIcon from '@material-ui/icons/Person';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import FacebookSharpIcon from '@mui/icons-material/FacebookSharp';
import InstagramIcon from '@material-ui/icons/Instagram';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import debounce from 'lodash/debounce';
import { useAppSelector } from 'app';
import CustomerAPI, { MergeSocialCustomerRequest } from 'components/customer/api/CustomerAPI';
import type { GetCustomersV2Dto } from 'common/models/customer';
import AppSnackbar from 'components/snackbar/snackbar';
import { formatPhoneNumber } from 'common/utils';
import EmailLink from 'common/components/buttons/email-link';

const useStyles = makeStyles(() => ({
  table: {
    minWidth: 750,
  },
  tableContainer: {
    maxHeight: '65vh',
  },
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  selectedRow: {
    backgroundColor: 'rgba(25, 118, 210, 0.12) !important',
  },
  nameCell: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
    padding: '0 12px 0 16px',
  },
  noResults: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#666',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '40px',
  },
}));

export interface SocialMediaCustomerListProps {
  /** The social media customer ID to merge into the selected customer */
  sourceCrmCustomerId: number;
}

const SocialMediaCustomerList: FC<SocialMediaCustomerListProps> = ({
  sourceCrmCustomerId,
}) => {
  const classes = useStyles();
  const [showSnackbar] = AppSnackbar.useSnackBar();
  
  const dealerId = useAppSelector((state) => state.user.dealerId);
  
  const [customers, setCustomers] = useState<GetCustomersV2Dto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<GetCustomersV2Dto | null>(null);
  const [merging, setMerging] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Fetch customers from API using V2
  const fetchCustomers = useCallback(async (
    searchValue: string,
    pageNumber: number
  ) => {
    if (!dealerId) return;
    
    setLoading(true);
    try {
      const response = await CustomerAPI.getCustomersV2({
        pageNumber: pageNumber + 1,
        pageSize: pageSize,
        searchString: searchValue,
      });
      
      // Filter out the source customer (the one being merged)
      const filteredCustomers = (response?.customers || []).filter(
        (customer) => customer.crmCustomerId !== sourceCrmCustomerId
      );
      
      setCustomers(filteredCustomers);
      setTotalCustomers(response?.totalRecords ? response.totalRecords - 1 : 0);
    } catch (error: any) {
      // Don't show error for aborted requests
      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return;
      }
      console.error('Error fetching customers:', error);
      showSnackbar({ type: 'error', message: 'Failed to load customers' });
      setCustomers([]);
      setTotalCustomers(0);
    } finally {
      setLoading(false);
    }
  }, [dealerId, pageSize, showSnackbar]);

  // Debounced search
  const debouncedFetch = useCallback(
    debounce((search: string, pg: number) => {
      fetchCustomers(search, pg);
    }, 500),
    [fetchCustomers]
  );

  // Single effect for fetching - handles both initial load and search/page changes
  useEffect(() => {
    debouncedFetch(searchString, page);
    return () => {
      debouncedFetch.cancel();
    };
  }, [searchString, page, debouncedFetch]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value);
    setPage(0); // Reset to first page on search
    setSelectedCustomer(null);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowClick = (customer: GetCustomersV2Dto) => {
    setSelectedCustomer(customer);
    setConfirmDialogOpen(true);
  };

  const handleConfirmDialogClose = () => {
    if (!merging) {
      setConfirmDialogOpen(false);
    }
  };

  const handleConfirmMerge = () => {
    handleMerge();
  };

  const handleMerge = async () => {
    if (!selectedCustomer || !dealerId) return;
    
    setMerging(true);
    try {
      const mergeReq: MergeSocialCustomerRequest = {
        dealerId: dealerId,
        socialCrmCustomerId: sourceCrmCustomerId,
        targetCrmCustomerId: selectedCustomer.crmCustomerId,
      };
      
      const response = await CustomerAPI.MergeSocialCustomer(mergeReq);
      
      if (response) {
        showSnackbar({ type: 'success', message: 'Customer merged successfully' });
        handleConfirmDialogClose();
        // Reload to reflect changes
        window.location.reload();
      } else {
        showSnackbar({ type: 'error', message: 'Failed to merge customer' });
      }
    } catch (error) {
      console.error('Error merging customer:', error);
      showSnackbar({ type: 'error', message: 'Error merging customer' });
    } finally {
      setMerging(false);
    }
  };

  return (
    <Box>
      {/* Search Field */}
      <TextField
        fullWidth
        style={{ backgroundColor: 'white', marginBottom: 16 }}
        margin="dense"
        variant="outlined"
        placeholder="Search customers by name, email, or phone..."
        value={searchString}
        InputProps={{
          startAdornment: <SearchIcon color="disabled" style={{ paddingRight: 8 }} />,
        }}
        onChange={handleSearchChange}
      />

      {/* Customer Table */}
      {loading ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : customers.length === 0 ? (
        <Box className={classes.noResults}>
          <PersonIcon style={{ fontSize: 48, color: '#ccc', marginBottom: 8 }} />
          <Typography variant="body1" color="textSecondary">
            {searchString ? 'No customers found matching your search' : 'No customers available'}
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader size="small" className={classes.table}>
              <TableHead style={{ backgroundColor: 'rgba(0,0,0,0.1)' }}>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Organization</strong></TableCell>
                  <TableCell><strong>Cell Phone</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Social Channels</strong></TableCell>
                  <TableCell><strong>Address</strong></TableCell>
                  <TableCell align="center"><strong>DMS Sync</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow
                    hover
                    key={customer.crmCustomerId}
                    onClick={() => handleRowClick(customer)}
                    className={`${classes.tableRow} ${
                      selectedCustomer?.crmCustomerId === customer.crmCustomerId ? classes.selectedRow : ''
                    }`}
                    selected={selectedCustomer?.crmCustomerId === customer.crmCustomerId}
                  >
                    <TableCell className={classes.nameCell} component="th" scope="row" padding="none">
                      {customer.name && customer.name.length > 30 
                        ? customer.name.slice(0, 30) + '...' 
                        : customer.name}
                    </TableCell>
                    <TableCell>{customer.organizationName || ''}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      {customer.isPhoneNumberVerified === true ? (
                        <CheckCircleIcon style={{ color: '#4caf50', fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                      ) : customer.isPhoneNumberVerified === false ? (
                        <CancelIcon style={{ color: '#f44336', fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                      ) : null}
                      {customer.phoneNumber ? formatPhoneNumber(customer.phoneNumber) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      {customer.emailAddress && <EmailLink email={customer.emailAddress} />}
                    </TableCell>
                    <TableCell>
                      <Grid container spacing={1} alignItems="center">
                        {customer.isFacebookCustomer && (
                          <Grid item>
                            <Tooltip title="Facebook">
                              <FacebookSharpIcon style={{ color: '#1877F2' }} />
                            </Tooltip>
                          </Grid>
                        )}
                        {customer.isInstagramCustomer && (
                          <Grid item>
                            <Tooltip title="Instagram">
                              <InstagramIcon style={{ color: '#E4405F' }} />
                            </Tooltip>
                          </Grid>
                        )}
                      </Grid>
                    </TableCell>
                    <TableCell>{customer.address || ''}</TableCell>
                    <TableCell align="center">
                      {customer.dmsSync ? (
                        <CheckCircleIcon style={{ color: '#4caf50' }} />
                      ) : (
                        <CancelIcon style={{ color: '#f44336' }} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCustomers}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[pageSize]}
          />
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        aria-labelledby="merge-confirm-dialog-title"
        aria-describedby="merge-confirm-dialog-description"
      >
        <DialogTitle id="merge-confirm-dialog-title">
          Connect Social Media Customer
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="merge-confirm-dialog-description">
            Are you sure you want to connect this social media customer to <strong>{selectedCustomer?.name}</strong>?
          </DialogContentText>
          <DialogContentText style={{ marginTop: '8px', fontSize: '0.875rem', color: '#666' }}>
            <em>Note: Only social media conversations will be merged.</em>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleConfirmDialogClose} 
            variant="contained"
            disabled={merging}
            style={{ backgroundColor: '#d32f2f', color: '#fff' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmMerge} 
            variant="contained"
            disabled={merging}
            style={{ backgroundColor: '#4caf50', color: '#fff' }}
          >
            {merging ? <CircularProgress size={20} color="inherit" /> : 'Yes, Connect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialMediaCustomerList;
